import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface Account {
    id: number;
    accountNumber: string;
    accountName: string;
}

export interface Vat {
    id: number;
    vatCode: string;
    vatName: string;
    vatRate: number;
    account: Account | null;
}

export async function getVat(id: number): Promise<Vat> {
    const result = await rms2Fetch<Vat>(`vats/${id}`);
    if (!result) throw new Error('Empty response for getVat');
    return result;
}

export async function createVat(v: Partial<Vat>): Promise<Vat> {
    const result = await rms2Fetch<Vat>('vats', {
        method: 'POST',
        body: JSON.stringify(v)
    });
    if (!result) throw new Error('Empty response for createVat');
    return result;
}

export async function updateVat(v: Vat): Promise<Vat> {
    const result = await rms2Fetch<Vat>(`vats/${v.id}`, {
        method: 'PUT',
        body: JSON.stringify(v)
    });
    if (!result) throw new Error('Empty response for updateVat');
    return result;
}

export async function deleteVat(id: number): Promise<void> {
    await rms2Fetch<void>(`vats/${id}`, { method: 'DELETE' });
}

export async function getAccounts(): Promise<Account[]> {
    const response = await rms2Fetch<{ accounts: Account[] }>('vats/accounts');
    return response?.accounts ?? [];
}
